import { Constructor } from '@angular/cdk/table';
import { FormGroup, FormBuilder, FormControl, FormArray } from '@angular/forms';
import { Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { AuthorService } from '../services/author.service';
import { IAuthor } from 'src/app/core/models/i-author';
import { compareArrays } from '../utils/array-utils';
import { IAuthorResponse } from 'src/app/core/models/i-author-response';
import { IAutoCompleteData } from 'src/app/shared-form-sections/interfaces/i-auto-complete-data';

type WithAuthorsFormSectionType = {
  authorList: IAuthor[];
  searchList: IAutoCompleteData[];
  searchedAuthor: FormControl;
  isSearchingAuthors: boolean;
  _initAuthors(selectedAuthors: IAuthor[]);
  _initSelectedAuthorsValueChanges(
    getPreviousSelectedValue: () => IAuthor[] | null,
  );
  _buildSelectedAuthors();
};

type WithAuthorsFormSectionRequerimentsBase = {
  form: FormGroup;
  FORM_PATHS: { SELECTED_AUTHORS: () => string };
  destroy$: Subject<boolean>;
  authorService: AuthorService;
  formBuilder: FormBuilder;
};

export function WithAuthorsFormSection<
  TBase extends Constructor<WithAuthorsFormSectionRequerimentsBase>,
>(
  baseClass: TBase,
): {
  new (...args: any[]): WithAuthorsFormSectionType;
  prototype: WithAuthorsFormSectionType;
} & TBase {
  return class extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    authorList: IAuthor[];
    searchList: IAutoCompleteData[];
    searchedAuthor: FormControl = this.formBuilder.control('');
    isSearchingAuthors: boolean;

    _initAuthors(selectedAuthors: IAuthor[]): void {
      let savedAuthors = this.form.get(
        this.FORM_PATHS.SELECTED_AUTHORS(),
      ) as FormArray<FormControl<IAuthor>>;

      selectedAuthors.forEach((authorItem) => {
        savedAuthors.push(new FormControl(authorItem));
      });

      this.searchedAuthor.valueChanges
        .pipe(
          distinctUntilChanged(),
          debounceTime(500),
          tap(() => {
            this.isSearchingAuthors = true;
          }),
          switchMap((searchedValue) => {
            const getDataObs$ = searchedValue
              ? this.authorService
                  .getFilteredBySearchText$(
                    typeof searchedValue === 'string' ? searchedValue : '',
                  )
                  .pipe(
                    map(
                      (authorsResponse: IAuthorResponse) =>
                        authorsResponse.results,
                    ),
                  )
              : of(null);

            return getDataObs$.pipe(
              finalize(() => {
                this.isSearchingAuthors = false;
              }),
            );
          }),
        )
        .subscribe({
          next: (x) => {
            if (x) {
              this.authorList = x;
              this.searchList = x.map((p) => {
                return { displayName: p.name, id: p.id };
              });
            }
          },
        });
    }

    _initSelectedAuthorsValueChanges(
      getPreviousSelectedValue: () => IAuthor[] | null,
    ): void {
      this.form
        .get(this.FORM_PATHS.SELECTED_AUTHORS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousSelectedValue())) {
              this.form
                .get(this.FORM_PATHS.SELECTED_AUTHORS())
                .markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.SELECTED_AUTHORS()).markAsDirty();
            }
          },
        });
    }

    _buildSelectedAuthors(): IAuthor[] {
      return this.form.get(this.FORM_PATHS.SELECTED_AUTHORS()).value;
    }
  };
}
